import { useEffect, useState, useCallback, useRef, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_BREATH_PROFILE,
  EVIDENCE_LABELS,
  EVENT_KINDS,
  KIND_FIELDS,
  LEVEL_COPY,
  SOURCE_LABELS,
  kindIcon,
  nextActionForProfile,
  parseBreathProfilePayload,
  analyticsForProfile,
  referralSignalsForProfile,
  regimeForProfile,
  scoreBreathLevel,
  weeklyPlanForProfile,
  type BreathEvidence,
  type BreathEvidenceAttachment,
  type BreathLevel,
  type BreathProfile,
  type BreathSource,
} from '@breathly/shared';
import { useAuth } from '../lib/auth.js';
import {
  api,
  type DailyPlanDraftResponse,
  type DailyPlanResponse,
  type ExperimentOutcome,
  type ExperimentResponse,
  type EventResponse,
  type EventListResponse,
  type KnowledgeDraftResponse,
  type KnowledgeResponse,
  type ObservationDraftResponse,
  type ObservationResponse,
} from '../lib/api.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface DayGroup {
  readonly label: string;
  readonly date: string;
  readonly events: readonly EventResponse[];
}

type ExperimentCompletionOutcome = Exclude<ExperimentOutcome, 'abandoned'>;

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function groupByDay(events: readonly EventResponse[]): DayGroup[] {
  const groups = new Map<string, EventResponse[]>();

  for (const ev of events) {
    const dateKey = ev.occurred_at.slice(0, 10);
    const existing = groups.get(dateKey) ?? [];
    existing.push(ev);
    groups.set(dateKey, existing);
  }

  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const yesterdayStr = new Date(today.getTime() - 86400000).toISOString().slice(0, 10);

  const result: DayGroup[] = [];
  const sortedDates = [...groups.keys()].sort((a, b) => b.localeCompare(a));

  for (const date of sortedDates) {
    const evs = groups.get(date)!;
    evs.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));

    let label: string;
    if (date === todayStr) {
      label = 'Today';
    } else if (date === yesterdayStr) {
      label = 'Yesterday';
    } else {
      const d = new Date(date + 'T12:00:00Z');
      label = d.toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    }

    result.push({ label, date, events: evs });
  }

  return result;
}

interface BreathQuickLogTemplate {
  readonly label: string;
  readonly icon: string;
  readonly kind: string;
  readonly payload: Record<string, unknown>;
  readonly context?: string;
}

const BREATH_QUICK_LOGS: readonly BreathQuickLogTemplate[] = [
  {
    label: 'Breath check',
    icon: '◎',
    kind: 'symptom',
    payload: {
      symptom_type: 'breath_check',
      severity: 3,
      breath_concern: 3,
      confidence: 7,
      description: 'Quick breath confidence check.',
    },
    context: 'Logged from breath prep.',
  },
  {
    label: 'Tongue clean',
    icon: '✓',
    kind: 'oral-care',
    payload: {
      oral_care_type: 'tongue_clean',
      tongue_cleaned: true,
      duration_minutes: 1,
      description: 'Tongue cleaning completed.',
    },
  },
  {
    label: 'Water',
    icon: '+',
    kind: 'hydration',
    payload: {
      volume_ml: 350,
      type: 'water',
      description: 'Water for dry-mouth prevention.',
    },
  },
  {
    label: 'Dry mouth',
    icon: '!',
    kind: 'symptom',
    payload: {
      symptom_type: 'dry_mouth',
      severity: 5,
      breath_concern: 5,
      dry_mouth: 6,
      description: 'Dry mouth noticed.',
    },
  },
  {
    label: 'Coffee',
    icon: 'C',
    kind: 'beverage',
    payload: {
      beverage_type: 'coffee',
      volume_ml: 250,
      water_after: false,
      description: 'Coffee logged. Pair with water if possible.',
    },
  },
  {
    label: 'Social prep',
    icon: 'S',
    kind: 'social',
    payload: {
      social_type: 'close_contact_plan',
      prep_needed: true,
      confidence_goal: 8,
      description: 'Upcoming close-contact moment.',
    },
    context: 'Plan prep routine before this.',
  },
] as const;

function payloadNumber(event: EventResponse, key: string): number | null {
  const value = event.payload?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function latestPayloadNumber(events: readonly EventResponse[], key: string): number | null {
  for (const event of events) {
    const value = payloadNumber(event, key);
    if (value !== null) return value;
  }
  return null;
}

function todayEvents(events: readonly EventResponse[]): EventResponse[] {
  const today = new Date().toISOString().slice(0, 10);
  return events.filter((event) => event.occurred_at.slice(0, 10) === today && !event.archived_at);
}

function latestStatement(
  observations: readonly ObservationResponse[],
  experiments: readonly ExperimentResponse[],
): string {
  if (observations[0]?.statement) return observations[0].statement;
  if (experiments[0]?.hypothesis) return experiments[0].hypothesis;
  return 'Start with a breath check, water, and one oral-care note so Breathly can learn your personal triggers.';
}

function latestBreathProfile(events: readonly EventResponse[]): BreathProfile {
  const profileEvent = events.find(
    (event) => event.kind === 'journal' && event.payload?.profile_type === 'breath-profile',
  );
  return profileEvent ? parseBreathProfilePayload(profileEvent.payload) : DEFAULT_BREATH_PROFILE;
}

function BreathProfileCard({
  profile,
  onOpen,
}: {
  readonly profile: BreathProfile;
  readonly onOpen: () => void;
}): JSX.Element {
  return (
    <section className="profile-card">
      <div className="profile-card-copy">
        <span className="profile-eyebrow">Breath profile</span>
        <h2>{LEVEL_COPY[profile.level].label}</h2>
        <p>{LEVEL_COPY[profile.level].summary}</p>
      </div>
      <div className="profile-score-row">
        <span>Odor {profile.odorConcern}/10</span>
        <span>Anxiety {profile.anxiety}/10</span>
        <span>Avoidance {profile.socialAvoidance}/10</span>
      </div>
      <div className="profile-pills">
        {profile.sources.slice(0, 4).map((source) => (
          <span key={source}>{SOURCE_LABELS[source]}</span>
        ))}
      </div>
      <button type="button" className="profile-edit-btn" onClick={onOpen}>
        Update profile
      </button>
    </section>
  );
}

function TailoredRegimePanel({ profile }: { readonly profile: BreathProfile }): JSX.Element {
  const analytics = analyticsForProfile(profile);
  const referrals = referralSignalsForProfile(profile);
  const [weeklyAction, setWeeklyAction] = useState<string | null>(null);

  async function saveWeeklyPlan(status: 'accepted' | 'skipped' | 'changed') {
    setWeeklyAction(status);
    await api.createEvent({
      kind: 'journal',
      occurred_at: new Date().toISOString(),
      payload: {
        profile_type: 'weekly-breath-plan',
        status,
        level: profile.level,
        source_focus: profile.sources[0] ?? 'unknown',
        items: weeklyPlanForProfile(profile),
        nextAction: profile.nextAction,
        description: `Weekly breath plan ${status}.`,
      },
      context: 'Saved from tailored weekly Breathly plan.',
    });
  }
  return (
    <section className="regime-panel">
      <div className="regime-header">
        <div>
          <span className="profile-eyebrow">Tailored hygiene regime</span>
          <h2>{profile.goal}</h2>
        </div>
        <strong>{profile.anxiety}/10 anxiety</strong>
      </div>
      <p className="regime-next-action">{profile.nextAction}</p>
      <div className="diagnostic-grid">
        <article>
          <span>Evidence</span>
          <strong>{analytics.evidenceStrength}/10</strong>
          <p>Higher when reports, photos, feedback, or routine response exist.</p>
        </article>
        <article>
          <span>Anxiety gap</span>
          <strong>{analytics.anxietyGap}/10</strong>
          <p>Flags possible halitophobia pattern when concern exceeds evidence.</p>
        </article>
      </div>
      <div className="regime-steps">
        {regimeForProfile(profile).map((step, index) => (
          <details key={step} className="regime-step" open={index < 3}>
            <summary>
              <span>{String(index + 1).padStart(2, '0')}</span>
              {step}
            </summary>
            <p>
              Track whether this changes breath concern, confidence, or social avoidance. Breathly
              should learn from evidence, not guess.
            </p>
          </details>
        ))}
      </div>
      <section className="weekly-plan-panel">
        <span className="profile-eyebrow">7-day adaptive plan</span>
        <ol>
          {weeklyPlanForProfile(profile).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
        <div className="weekly-plan-actions">
          <button type="button" onClick={() => void saveWeeklyPlan('accepted')}>
            Save plan
          </button>
          <button type="button" onClick={() => void saveWeeklyPlan('changed')}>
            Mark changed
          </button>
          <button type="button" onClick={() => void saveWeeklyPlan('skipped')}>
            Skip
          </button>
        </div>
        {weeklyAction && <p className="inline-status">Weekly plan saved as {weeklyAction}.</p>}
      </section>
      <section className="guardrail-panel">
        <span className="profile-eyebrow">Guardrails</span>
        <p>
          Breathly is a tracking and behavior-support tool, not a diagnosis. Use it to prepare,
          collect evidence, and decide when dental, ENT, reflux, or anxiety support is appropriate.
        </p>
        {referrals.length > 0 && (
          <ul>
            {referrals.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function RetentionPanel({ profile }: { readonly profile: BreathProfile }): JSX.Element {
  const [savedReminder, setSavedReminder] = useState<string | null>(null);

  async function saveReminder(label: string) {
    setSavedReminder(label);
    await api.createEvent({
      kind: 'journal',
      occurred_at: new Date().toISOString(),
      payload: {
        profile_type: 'breath-reminder-preference',
        reminder: label,
        level: profile.level,
        source_focus: profile.sources[0] ?? 'unknown',
        description: `Reminder preference saved: ${label}.`,
      },
      context: 'Saved from Breathly reminder loop.',
    });
  }

  return (
    <section className="retention-panel">
      <div>
        <span className="profile-eyebrow">Reminder loop</span>
        <h2>Daily breath confidence check</h2>
        <p>
          Morning baseline, post-coffee hydration, and pre-close-contact confidence are the three
          reminders that make Breathly useful week after week.
        </p>
      </div>
      <div className="retention-actions">
        {['Morning baseline', 'After coffee', 'Before close contact'].map((label) => (
          <button key={label} type="button" onClick={() => void saveReminder(label)}>
            {label}
          </button>
        ))}
      </div>
      <strong>{analyticsForProfile(profile).priority}</strong>
      {savedReminder && <p className="inline-status">Reminder saved: {savedReminder}.</p>}
    </section>
  );
}

function BreathProfileModal({
  current,
  onClose,
  onSaved,
}: {
  readonly current: BreathProfile;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): JSX.Element {
  const [step, setStep] = useState(0);
  const [level, setLevel] = useState<BreathLevel>(current.level);
  const [odorConcern, setOdorConcern] = useState(current.odorConcern);
  const [anxiety, setAnxiety] = useState(current.anxiety);
  const [socialAvoidance, setSocialAvoidance] = useState(current.socialAvoidance);
  const [sources, setSources] = useState<BreathSource[]>([...current.sources]);
  const [symptoms, setSymptoms] = useState(current.symptoms.join(', '));
  const [evidence, setEvidence] = useState<BreathEvidence[]>([...current.evidence]);
  const [attachments, setAttachments] = useState<BreathEvidenceAttachment[]>([
    ...current.evidenceAttachments,
  ]);
  const [uploading, setUploading] = useState(false);
  const [goal, setGoal] = useState(current.goal);
  const [notes, setNotes] = useState(current.notes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleSource(source: BreathSource) {
    setSources((prev) =>
      prev.includes(source) ? prev.filter((item) => item !== source) : [...prev, source],
    );
  }

  function toggleEvidence(item: BreathEvidence) {
    setEvidence((prev) =>
      prev.includes(item) ? prev.filter((existing) => existing !== item) : [...prev, item],
    );
  }

  const computedLevel = scoreBreathLevel({ odorConcern, anxiety, socialAvoidance, evidence });
  const previewProfile: BreathProfile = {
    level,
    odorConcern,
    anxiety,
    socialAvoidance,
    sources,
    symptoms: symptoms
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
    evidence,
    evidenceAttachments: attachments,
    goal: goal.trim() || DEFAULT_BREATH_PROFILE.goal,
    notes: notes.trim(),
    nextAction: nextActionForProfile({ level, sources }),
  };

  async function saveProfile() {
    setSaving(true);
    setError(null);
    try {
      await api.createEvent({
        kind: 'journal',
        occurred_at: new Date().toISOString(),
        payload: {
          profile_type: 'breath-profile',
          level,
          computed_level: computedLevel,
          odorConcern,
          anxiety,
          socialAvoidance,
          sources,
          symptoms: previewProfile.symptoms,
          evidence,
          evidenceAttachments: attachments,
          goal: goal.trim(),
          notes: notes.trim(),
          nextAction: previewProfile.nextAction,
          description: 'Breath Profile intake updated.',
        },
        context: 'Profile intake for tailored halitosis regime.',
      });
      onSaved();
    } catch {
      setError('Could not save Breath Profile.');
    } finally {
      setSaving(false);
    }
  }

  async function handleEvidenceFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded = await api.uploadAttachment(file, evidence[0] ?? 'self-report');
      setAttachments((prev) => [
        ...prev,
        {
          key: uploaded.key,
          filename: uploaded.filename,
          contentType: uploaded.content_type,
          size: uploaded.size,
          evidenceType: uploaded.evidence_type,
          uploadedAt: uploaded.uploaded_at,
          downloadPath: uploaded.download_path,
        },
      ]);
      if (!evidence.includes('photo') && uploaded.content_type.startsWith('image/')) {
        setEvidence((prev) => [...prev, 'photo']);
      }
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'detail' in err ? String(err.detail) : '';
      setError(message || 'Could not upload evidence file.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content profile-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Breath Profile intake</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="profile-stepper">
          {['Severity', 'Sources', 'Evidence', 'Plan'].map((label, index) => (
            <button
              key={label}
              className={step === index ? 'profile-step-active' : ''}
              onClick={() => setStep(index)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>

        {step === 0 && (
          <div className="profile-step-page">
            <div className="profile-diagnostic-preview">
              <span>Computed classification</span>
              <strong>{LEVEL_COPY[computedLevel].label}</strong>
              <p>{LEVEL_COPY[computedLevel].summary}</p>
              {computedLevel !== level && (
                <button type="button" onClick={() => setLevel(computedLevel)}>
                  Use computed level
                </button>
              )}
            </div>
            <div className="profile-choice-grid">
              {(Object.keys(LEVEL_COPY) as BreathLevel[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={
                    level === key ? 'profile-choice profile-choice-active' : 'profile-choice'
                  }
                  onClick={() => setLevel(key)}
                >
                  <strong>{LEVEL_COPY[key].label}</strong>
                  <span>{LEVEL_COPY[key].summary}</span>
                </button>
              ))}
            </div>
            <label className="profile-range">
              Odor concern / likely intensity
              <input
                type="range"
                min={0}
                max={10}
                value={odorConcern}
                onChange={(event) => setOdorConcern(Number(event.target.value))}
              />
              <span>{odorConcern}/10</span>
            </label>
            <label className="profile-range">
              Breath anxiety / checking urge
              <input
                type="range"
                min={0}
                max={10}
                value={anxiety}
                onChange={(event) => setAnxiety(Number(event.target.value))}
              />
              <span>{anxiety}/10</span>
            </label>
            <label className="profile-range">
              Social avoidance
              <input
                type="range"
                min={0}
                max={10}
                value={socialAvoidance}
                onChange={(event) => setSocialAvoidance(Number(event.target.value))}
              />
              <span>{socialAvoidance}/10</span>
            </label>
          </div>
        )}

        {step === 1 && (
          <div className="profile-step-page">
            <div className="source-grid">
              {(Object.keys(SOURCE_LABELS) as BreathSource[]).map((source) => (
                <button
                  key={source}
                  type="button"
                  className={
                    sources.includes(source) ? 'source-chip source-chip-active' : 'source-chip'
                  }
                  onClick={() => toggleSource(source)}
                >
                  {SOURCE_LABELS[source]}
                </button>
              ))}
            </div>
            <label className="form-label">
              Symptoms / situations
              <textarea
                className="form-textarea"
                value={symptoms}
                onChange={(event) => setSymptoms(event.target.value)}
                rows={3}
              />
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="profile-step-page">
            <div className="evidence-dropzone">
              <strong>Photo / report / dentist note upload</strong>
              <span>
                Upload tongue photos, dentist notes, reflux/ENT reports, or routine evidence. Files
                are stored in private R2 object keys through the Worker API.
              </span>
              <input
                type="file"
                accept="image/*,.pdf,.txt,.md"
                onChange={(event) => void handleEvidenceFileChange(event)}
                disabled={uploading}
              />
              {uploading && <em>Uploading evidence...</em>}
            </div>
            {attachments.length > 0 && (
              <div className="attachment-list">
                {attachments.map((attachment) => (
                  <div key={attachment.key} className="attachment-item">
                    <strong>{attachment.filename}</strong>
                    <span>
                      {Math.max(1, Math.round(attachment.size / 1024))} KB ·{' '}
                      {attachment.evidenceType}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setAttachments((prev) => prev.filter((item) => item.key !== attachment.key))
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="source-grid">
              {(Object.keys(EVIDENCE_LABELS) as BreathEvidence[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  className={
                    evidence.includes(item) ? 'source-chip source-chip-active' : 'source-chip'
                  }
                  onClick={() => toggleEvidence(item)}
                >
                  {EVIDENCE_LABELS[item]}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="profile-step-page">
            <div className="profile-plan-preview">
              <strong>{LEVEL_COPY[level].label}</strong>
              <p>{previewProfile.nextAction}</p>
              <ol>
                {regimeForProfile(previewProfile).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            </div>
            <label className="form-label">
              Primary goal
              <input
                className="form-input"
                value={goal}
                onChange={(event) => setGoal(event.target.value)}
              />
            </label>
            <label className="form-label">
              Notes
              <textarea
                className="form-textarea"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
              />
            </label>
          </div>
        )}

        {error && <p className="form-error">{error}</p>}
        <div className="profile-modal-actions">
          <button
            type="button"
            className="form-btn form-btn-secondary"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
          >
            Back
          </button>
          {step < 3 ? (
            <button
              type="button"
              className="form-btn form-btn-primary"
              onClick={() => setStep(step + 1)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="form-btn form-btn-primary"
              onClick={saveProfile}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save profile'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BreathCommandCenter({
  events,
  observations,
  experiments,
  onCheckIn,
}: {
  readonly events: readonly EventResponse[];
  readonly observations: readonly ObservationResponse[];
  readonly experiments: readonly ExperimentResponse[];
  readonly onCheckIn: () => void;
}): JSX.Element {
  const activeEvents = events.filter((event) => !event.archived_at);
  const todaysEvents = todayEvents(events);
  const concern =
    latestPayloadNumber(activeEvents, 'breath_concern') ??
    latestPayloadNumber(activeEvents, 'severity') ??
    0;
  const confidence = latestPayloadNumber(activeEvents, 'confidence') ?? Math.max(1, 10 - concern);
  const readiness = Math.max(
    0,
    Math.min(100, Math.round((confidence / 10) * 70 + ((10 - concern) / 10) * 30)),
  );
  const oralCareCount = todaysEvents.filter((event) => event.kind === 'oral-care').length;
  const hydrationCount = todaysEvents.filter(
    (event) => event.kind === 'hydration' || event.payload?.beverage_type === 'water',
  ).length;
  const socialPlans = todaysEvents.filter(
    (event) => event.kind === 'social' && event.is_future,
  ).length;
  const activeTrial =
    experiments.find((experiment) => experiment.status === 'active') ?? experiments[0];

  return (
    <section className="breath-command">
      <div className="breath-command-main">
        <div className="breath-command-copy">
          <span className="breath-eyebrow">Halitosis helper</span>
          <h2>Today&apos;s breath prep</h2>
          <p>
            Track the signals that usually matter before close contact: dry mouth, tongue coating,
            coffee, reflux, oral care, and confidence.
          </p>
        </div>
        <div className="breath-readiness" aria-label="Breath readiness score">
          <span>{readiness}</span>
          <small>readiness</small>
        </div>
      </div>
      <div className="breath-command-grid">
        <div className="breath-metric">
          <span className="breath-metric-label">Latest concern</span>
          <strong>{concern || '-'}/10</strong>
          <small>lower is better</small>
        </div>
        <div className="breath-metric">
          <span className="breath-metric-label">Confidence</span>
          <strong>{confidence || '-'}/10</strong>
          <small>before social contact</small>
        </div>
        <div className="breath-metric">
          <span className="breath-metric-label">Prep today</span>
          <strong>{oralCareCount + hydrationCount}</strong>
          <small>
            {oralCareCount} care, {hydrationCount} water
          </small>
        </div>
        <div className="breath-metric">
          <span className="breath-metric-label">Close-contact plans</span>
          <strong>{socialPlans}</strong>
          <small>prep before leaving</small>
        </div>
      </div>
      <div className="breath-command-footer">
        <p>{latestStatement(observations, experiments)}</p>
        <div className="breath-command-actions">
          {activeTrial && <span>Trial: {activeTrial.hypothesis}</span>}
          <button type="button" className="breath-primary-btn" onClick={onCheckIn}>
            Check in
          </button>
        </div>
      </div>
    </section>
  );
}

function renderKindFields(
  kind: string,
  payload: Record<string, unknown>,
  onChange: (key: string, value: unknown) => void,
): JSX.Element | null {
  const fields = KIND_FIELDS[kind];
  if (!fields || fields.length === 0) return null;

  return (
    <>
      <div className="kind-fields-divider">Details</div>
      {fields.map((f) => (
        <label key={f.key} className="form-label">
          {f.label}
          {f.type === 'select' ? (
            <select
              className="form-select"
              value={typeof payload[f.key] === 'string' ? (payload[f.key] as string) : ''}
              onChange={(e) => onChange(f.key, e.target.value || null)}
            >
              <option value="">Select...</option>
              {f.options?.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="form-input"
              type="number"
              min={f.min}
              max={f.max}
              step={f.step}
              value={typeof payload[f.key] === 'number' ? (payload[f.key] as number) : ''}
              onChange={(e) => {
                const v = e.target.value;
                onChange(f.key, v === '' ? null : Number(v));
              }}
            />
          )}
        </label>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Event Card
// ---------------------------------------------------------------------------

function EventCard({
  event,
  onShowDetail,
  selected,
  onToggleSelect,
  selectMode,
}: {
  event: EventResponse;
  onShowDetail: (ev: EventResponse) => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  selectMode?: boolean;
}): JSX.Element {
  function handleClick() {
    if (selectMode) {
      onToggleSelect?.(event.id);
    } else {
      onShowDetail(event);
    }
  }
  const icon = kindIcon(event.kind);
  const time = formatTime(event.occurred_at);

  const payload = event.payload ?? {};
  let title = event.kind;
  if (payload.description && typeof payload.description === 'string') {
    title = payload.description;
  } else if (payload.type && typeof payload.type === 'string') {
    title = `${event.kind}: ${payload.type}`;
  }

  const details: string[] = [];
  if (payload.hours && typeof payload.hours === 'number') {
    details.push(`${payload.hours}h`);
  }
  if (payload.duration_minutes && typeof payload.duration_minutes === 'number') {
    details.push(`${payload.duration_minutes}min`);
  }
  if (payload.distance_km && typeof payload.distance_km === 'number') {
    details.push(`${payload.distance_km.toFixed(1)}km`);
  }
  if (payload.calories_est && typeof payload.calories_est === 'number') {
    details.push(`~${payload.calories_est}cal`);
  }
  if (payload.quality && typeof payload.quality === 'string') {
    details.push(payload.quality);
  }
  if (payload.mood && typeof payload.mood === 'string') {
    details.push(payload.mood);
  }
  if (payload.severity !== undefined && typeof payload.severity === 'number') {
    if (payload.severity > 0) details.push(`severity: ${payload.severity}`);
  }

  return (
    <div
      className={`event-card event-kind-${event.kind}${selected ? ' event-card-selected' : ''}${event.archived_at ? ' event-card-archived' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick();
      }}
    >
      {selectMode && (
        <span className={`event-checkbox${selected ? ' event-checkbox-active' : ''}`}>
          {selected && <span className="event-checkmark">\u2713</span>}
        </span>
      )}
      <span className="event-icon">{icon}</span>
      <div className="event-body">
        <div className="event-header">
          <span className="event-title">{title}</span>
          <span className="event-time">{time}</span>
        </div>
        {details.length > 0 && (
          <div className="event-details">
            {details.map((d, i) => (
              <span key={i} className="event-detail-tag">
                {d}
              </span>
            ))}
          </div>
        )}
        {event.context && <p className="event-context">{event.context}</p>}
        {event.is_future && <span className="event-future-badge">Planned</span>}
        {event.archived_at && <span className="event-archived-badge">Archived</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Event Form (modal)
// ---------------------------------------------------------------------------

interface AddEventFormProps {
  readonly initialKind?: string | null;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function AddEventForm({ initialKind, onClose, onCreated }: AddEventFormProps): JSX.Element {
  const [kind, setKind] = useState(initialKind ?? '');
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 16));
  const [description, setDescription] = useState('');
  const [context, setContext] = useState('');
  const [isFuture, setIsFuture] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [kindPayload, setKindPayload] = useState<Record<string, unknown>>({});

  // Reset kind-specific fields when kind changes
  useEffect(() => {
    setKindPayload({});
    setDescription('');
  }, [kind]);

  function onKindFieldChange(key: string, value: unknown) {
    setKindPayload((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!kind) {
      setError('Please select an event kind.');
      return;
    }
    if (!occurredAt) {
      setError('Please set the date and time.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = { ...kindPayload };
      if (description.trim()) payload.description = description.trim();

      await api.createEvent({
        kind,
        occurred_at: new Date(occurredAt).toISOString(),
        payload,
        context: context.trim() || null,
        is_future: isFuture,
      });
      onCreated();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'detail' in err) {
        setError((err as { detail: string }).detail);
      } else {
        setError('Failed to create event. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Log an event</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form className="event-form" onSubmit={handleSubmit}>
          <label className="form-label">
            Kind
            <select
              className="form-select"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              required
            >
              <option value="">Select kind...</option>
              {EVENT_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.icon} {k.label}
                </option>
              ))}
            </select>
          </label>

          <label className="form-label">
            When it happened
            <input
              className="form-input"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              required
            />
          </label>

          {kind && renderKindFields(kind, kindPayload, onKindFieldChange)}

          <label className="form-label">
            Description
            <input
              className="form-input"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Oatmeal with berries"
            />
          </label>

          <label className="form-label">
            Context / notes
            <textarea
              className="form-textarea"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="How were you feeling? Any observations?"
              rows={3}
            />
          </label>

          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={isFuture}
              onChange={(e) => setIsFuture(e.target.checked)}
            />
            <span>This is a planned (future) event</span>
          </label>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="form-btn form-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="form-btn form-btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Log event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EmptyTimelineStateProps {
  readonly hasFilters: boolean;
  readonly message: string;
  readonly onClearFilters: () => void;
  readonly onStartWithKind: (kind: string) => void;
}

function EmptyTimelineState({
  hasFilters,
  message,
  onClearFilters,
  onStartWithKind,
}: EmptyTimelineStateProps): JSX.Element {
  if (hasFilters) {
    return (
      <div className="dashboard-empty dashboard-empty-panel">
        <p className="dashboard-empty-title">{message}</p>
        <p className="dashboard-empty-copy">
          Try widening the filters, or log a new event from what is happening today.
        </p>
        <div className="dashboard-empty-actions">
          <button className="secondary-action-btn" onClick={onClearFilters}>
            Clear filters
          </button>
          <button className="primary-action-btn" onClick={() => onStartWithKind('symptom')}>
            Log symptom
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-empty dashboard-empty-panel">
      <p className="dashboard-empty-title">Start with one small note.</p>
      <p className="dashboard-empty-copy">
        Breathly works best when the first entries are simple evidence: a symptom, oral care, or a
        short journal note.
      </p>
      <div className="dashboard-empty-actions">
        <button className="primary-action-btn" onClick={() => onStartWithKind('symptom')}>
          Log symptom
        </button>
        <button className="secondary-action-btn" onClick={() => onStartWithKind('oral_care')}>
          Oral care
        </button>
        <button className="secondary-action-btn" onClick={() => onStartWithKind('journal')}>
          Journal note
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Event Form (modal)
// ---------------------------------------------------------------------------

interface EditEventFormProps {
  readonly event: EventResponse;
  readonly onClose: () => void;
  readonly onUpdated: () => void;
  readonly onDeleted?: (event: EventResponse) => void;
}

function EditEventForm({ event, onClose, onUpdated, onDeleted }: EditEventFormProps): JSX.Element {
  const [description, setDescription] = useState(
    typeof event.payload?.description === 'string' ? event.payload.description : '',
  );
  const [context, setContext] = useState(event.context ?? '');
  const [isFuture, setIsFuture] = useState(event.is_future);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [kindPayload, setKindPayload] = useState<Record<string, unknown>>({ ...event.payload });
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Sync kindPayload when event changes
  useEffect(() => {
    setKindPayload({ ...event.payload });
    setDescription(typeof event.payload?.description === 'string' ? event.payload.description : '');
    setContext(event.context ?? '');
    setIsFuture(event.is_future);
    setError(null);
    setConfirmDelete(false);
  }, [event]);

  function onKindFieldChange(key: string, value: unknown) {
    setKindPayload((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = { ...kindPayload };
      if (description.trim()) {
        payload.description = description.trim();
      } else {
        delete payload.description;
      }

      await api.updateEvent(event.id, {
        payload,
        context: context.trim() || null,
        is_future: isFuture,
      });
      onUpdated();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'detail' in err) {
        setError((err as { detail: string }).detail);
      } else {
        setError('Failed to update event. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      if (onDeleted) {
        // Delegate to parent for undo support — no immediate API delete
        await onDeleted(event);
      } else {
        await api.deleteEvent(event.id);
        onUpdated();
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'detail' in err) {
        setError((err as { detail: string }).detail);
      } else {
        setError('Failed to delete event. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Edit event</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form className="event-form" onSubmit={handleSave}>
          <div className="form-readonly-field">
            <span className="form-readonly-label">Kind</span>
            <span className="form-readonly-value">
              {kindIcon(event.kind)} {event.kind}
            </span>
          </div>

          <div className="form-readonly-field">
            <span className="form-readonly-label">When</span>
            <span className="form-readonly-value">
              {new Date(event.occurred_at).toLocaleString([], {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </span>
          </div>

          {renderKindFields(event.kind, kindPayload, onKindFieldChange)}

          <label className="form-label">
            Description
            <input
              className="form-input"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Oatmeal with berries"
            />
          </label>

          <label className="form-label">
            Context / notes
            <textarea
              className="form-textarea"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="How were you feeling? Any observations?"
              rows={3}
            />
          </label>

          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={isFuture}
              onChange={(e) => setIsFuture(e.target.checked)}
            />
            <span>This is a planned (future) event</span>
          </label>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions form-actions-split">
            <button
              type="button"
              className="form-btn form-btn-danger"
              onClick={handleDelete}
              disabled={submitting || deleting}
            >
              {deleting ? 'Deleting...' : confirmDelete ? 'Confirm delete?' : 'Delete'}
            </button>
            <div className="form-actions-right">
              <button type="button" className="form-btn form-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="form-btn form-btn-primary"
                disabled={submitting || deleting}
              >
                {submitting ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event Detail View (read-only modal)
// ---------------------------------------------------------------------------

interface EventDetailViewProps {
  readonly event: EventResponse;
  readonly onClose: () => void;
  readonly onEdit: () => void;
  readonly onDeleted?: (event: EventResponse) => void;
  readonly onArchived?: (event: EventResponse) => void;
}

// Known field labels for payload display
const KNOWN_FIELDS: Record<string, string> = {
  description: 'Description',
  hours: 'Hours',
  quality: 'Quality',
  exercise_type: 'Exercise type',
  duration_minutes: 'Duration (min)',
  distance_km: 'Distance (km)',
  calories_est: 'Calories (est.)',
  meal_type: 'Meal type',
  beverage_type: 'Type',
  volume_ml: 'Volume (ml)',
  symptom_type: 'Symptom type',
  severity: 'Severity',
  mood: 'Mood',
  intensity: 'Intensity',
  treatment_type: 'Type',
  oral_care_type: 'Care type',
  medical_type: 'Type',
  social_type: 'Type',
  journal_type: 'Type',
  travel_type: 'Type',
};

function formatPayloadValue(key: string, val: unknown): string {
  if (typeof val === 'number') {
    if (key === 'distance_km') return `${(val as number).toFixed(1)} km`;
    if (key === 'hours') return `${val} hr`;
    if (key === 'duration_minutes') return `${val} min`;
    if (key === 'volume_ml') return `${val} ml`;
    if (key === 'calories_est') return `~${val} cal`;
    if (key === 'severity') return `${val}/10`;
    if (key === 'intensity') return `${val}/10`;
    return String(val);
  }
  return String(val);
}

function EventDetailView({
  event,
  onClose,
  onEdit,
  onDeleted,
  onArchived,
}: EventDetailViewProps): JSX.Element {
  async function handleDuplicate() {
    try {
      const payload = { ...event.payload };
      delete payload.description;
      await api.createEvent({
        kind: event.kind,
        occurred_at: new Date().toISOString(),
        payload,
        context: event.context,
      });
      onClose();
    } catch {
      // silently fail
    }
  }

  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  // Reset confirm on new event
  useEffect(() => {
    setConfirmArchive(false);
  }, [event]);

  async function handleArchiveToggle() {
    if (event.archived_at) {
      // Restore — immediate, no confirmation needed
      setArchiving(true);
      try {
        await api.restoreEvent(event.id);
        onClose();
      } catch {
        // silently fail
      } finally {
        setArchiving(false);
      }
      return;
    }

    // Archive — needs double-tap confirmation since undo is time-limited
    if (!confirmArchive) {
      setConfirmArchive(true);
      return;
    }

    setArchiving(true);
    try {
      if (onArchived) {
        await onArchived(event);
        onClose();
      } else {
        await api.archiveEvent(event.id);
        onClose();
      }
    } catch {
      // silently fail
    } finally {
      setArchiving(false);
      setConfirmArchive(false);
    }
  }

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const payload = event.payload ?? {};

  // Build payload entries (skip null/empty)
  const payloadEntries: { key: string; label: string; value: string }[] = [];
  for (const [key, val] of Object.entries(payload)) {
    if (val === null || val === undefined || val === '') continue;
    const label = KNOWN_FIELDS[key] ?? key;
    payloadEntries.push({ key, label, value: formatPayloadValue(key, val) });
  }

  const fullDate = new Date(event.occurred_at).toLocaleString([], {
    dateStyle: 'full',
    timeStyle: 'short',
  });

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      if (onDeleted) {
        await onDeleted(event);
      } else {
        await api.deleteEvent(event.id);
        onClose();
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'detail' in err
          ? (err as { detail: string }).detail
          : 'Failed to delete event.';
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Event details</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Kind + timestamp header */}
        <div className="detail-header-row">
          <span className={`detail-icon detail-icon-${event.kind}`}>{kindIcon(event.kind)}</span>
          <div className="detail-header-info">
            <span className="detail-kind">{event.kind}</span>
            <span className="detail-timestamp">{fullDate}</span>
          </div>
        </div>

        {/* Payload entries */}
        {payloadEntries.length > 0 && (
          <div className="detail-section">
            <h3 className="detail-section-title">Details</h3>
            <div className="detail-payload">
              {payloadEntries.map((entry) => (
                <div key={entry.key} className="detail-field">
                  <span className="detail-field-label">{entry.label}</span>
                  <span className="detail-field-value">{entry.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Context */}
        {event.context && (
          <div className="detail-section">
            <h3 className="detail-section-title">Context / notes</h3>
            <p className="detail-context">{event.context}</p>
          </div>
        )}

        {/* Archived notice banner */}
        {event.archived_at && (
          <div className="detail-archived-notice">
            This event is archived and hidden from the default timeline.
          </div>
        )}

        {/* Future badge */}
        {event.is_future && (
          <div className="detail-section">
            <span className="event-future-badge">Planned event</span>
          </div>
        )}

        {/* Timestamps */}
        <div className="detail-section">
          <h3 className="detail-section-title">Times</h3>
          <div className="detail-field">
            <span className="detail-field-label">Recorded</span>
            <span className="detail-field-value">
              {new Date(event.recorded_at).toLocaleString([], {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </span>
          </div>
          {event.updated_at !== event.recorded_at && (
            <div className="detail-field">
              <span className="detail-field-label">Updated</span>
              <span className="detail-field-value">
                {new Date(event.updated_at).toLocaleString([], {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </span>
            </div>
          )}
        </div>

        {/* Duplicate button */}
        <button className="detail-duplicate-btn" onClick={handleDuplicate}>
          Duplicate as new
        </button>

        {/* Archive / Restore button */}
        <button
          className={`detail-archive-btn ${event.archived_at ? 'detail-archive-btn-restore' : ''} ${!event.archived_at && confirmArchive ? 'detail-archive-btn-confirm' : ''}`}
          onClick={handleArchiveToggle}
          disabled={archiving}
        >
          {archiving
            ? '...'
            : event.archived_at
              ? 'Restore event'
              : confirmArchive
                ? 'Tap again to archive'
                : 'Archive event'}
        </button>

        {/* Edit button */}
        <button className="detail-edit-btn" onClick={onEdit}>
          Edit event
        </button>

        {/* Delete */}
        {deleteError && <p className="form-error">{deleteError}</p>}
        <button
          className={`detail-delete-btn ${confirmDelete ? 'detail-delete-btn-confirm' : ''}`}
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : confirmDelete ? 'Tap again to delete' : 'Delete event'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Undo Delete Toast (purely presentational — parent owns the timer)
// ---------------------------------------------------------------------------

interface UndoToastProps {
  readonly kind: string;
  readonly action: 'deleted' | 'archived';
  readonly visible: boolean;
  readonly expiresAt: number;
  readonly onUndo: () => void;
}

function UndoToast({
  kind,
  action,
  visible,
  expiresAt,
  onUndo,
}: UndoToastProps): JSX.Element | null {
  const [secondsLeft, setSecondsLeft] = useState(5);

  useEffect(() => {
    if (!visible) {
      setSecondsLeft(5);
      return;
    }

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
    }, 200);

    return () => clearInterval(interval);
  }, [visible, expiresAt]);

  if (!visible) return null;

  return (
    <div className="undo-toast">
      <span className="undo-toast-text">
        {kind} event {action}. Undo ({secondsLeft}s)
      </span>
      <button className="undo-toast-btn" onClick={onUndo}>
        Undo
      </button>
    </div>
  );
}

function QuickLogRow({ onLogged }: { onLogged: () => void }): JSX.Element {
  const [quickLogging, setQuickLogging] = useState<string | null>(null);

  return (
    <div className="quick-log-row">
      {BREATH_QUICK_LOGS.map((tpl) => (
        <button
          key={tpl.label}
          className={`quick-log-btn ${quickLogging === tpl.label ? 'quick-log-btn-loading' : ''}`}
          title={`Log ${tpl.label}`}
          disabled={quickLogging !== null}
          onClick={async () => {
            setQuickLogging(tpl.label);
            try {
              await api.createEvent({
                kind: tpl.kind,
                occurred_at: new Date().toISOString(),
                payload: tpl.payload,
                context: tpl.context ?? null,
              });
              onLogged();
            } catch {
              // silently fail
            } finally {
              setQuickLogging(null);
            }
          }}
        >
          <span className="quick-log-icon">{quickLogging === tpl.label ? '...' : tpl.icon}</span>
          <span className="quick-log-label">{tpl.label}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kind Filter
// ---------------------------------------------------------------------------

interface KindFilterProps {
  readonly value: string;
  readonly onChange: (kind: string) => void;
}

function KindFilter({ value, onChange }: KindFilterProps): JSX.Element {
  return (
    <select className="kind-filter" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">All events</option>
      {EVENT_KINDS.map((k) => (
        <option key={k.value} value={k.value}>
          {k.icon} {k.label}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Date Range Filter
// ---------------------------------------------------------------------------

interface DateRangeFilterProps {
  readonly startDate: string;
  readonly endDate: string;
  readonly onStartChange: (val: string) => void;
  readonly onEndChange: (val: string) => void;
}

function DateRangeFilter({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
}: DateRangeFilterProps): JSX.Element {
  return (
    <div className="daterange-filter">
      <label className="daterange-label">
        From
        <input
          className="form-input daterange-input"
          type="date"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
        />
      </label>
      <label className="daterange-label">
        To
        <input
          className="form-input daterange-input"
          type="date"
          value={endDate}
          onChange={(e) => onEndChange(e.target.value)}
        />
      </label>
      {(startDate || endDate) && (
        <button
          className="daterange-clear"
          onClick={() => {
            onStartChange('');
            onEndChange('');
          }}
          title="Clear date range"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <input
      className="form-input search-input"
      type="search"
      placeholder="Search breath logs..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ---------------------------------------------------------------------------
// Summary View (computed stats from events)
// ---------------------------------------------------------------------------

interface StatsSummary {
  readonly totalEvents: number;
  readonly periodDays: number;
  readonly sleepTotalHours: number;
  readonly sleepAvgHours: number;
  readonly sleepDays: number;
  readonly exerciseCount: number;
  readonly exerciseTotalMin: number;
  readonly hydrationTotalMl: number;
  readonly symptomCount: number;
  readonly streakDays: number;
  readonly moodCounts: Record<string, number>;
}

function computeStats(events: readonly EventResponse[]): StatsSummary {
  // Exclude archived events from stats — they're historical, not current.
  const activeEvents = events.filter((e) => !e.archived_at);
  const now = new Date();
  const sorted = [...activeEvents].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  const oldest = sorted.length > 0 ? new Date(sorted[0]!.occurred_at) : now;
  const periodDays = Math.max(1, Math.round((now.getTime() - oldest.getTime()) / 86400000));

  let sleepTotalHours = 0;
  let sleepDays = 0;
  let exerciseCount = 0;
  let exerciseTotalMin = 0;
  let hydrationTotalMl = 0;
  let symptomCount = 0;
  const moodCounts: Record<string, number> = {};

  // Streak: consecutive days with at least one event going backwards from today
  const eventDates = new Set<string>();
  for (const ev of activeEvents) {
    eventDates.add(ev.occurred_at.slice(0, 10));
    const p = ev.payload || {};

    if (ev.kind === 'sleep' && typeof p.hours === 'number') {
      sleepTotalHours += p.hours;
      sleepDays++;
    }
    if (ev.kind === 'exercise') {
      exerciseCount++;
      if (typeof p.duration_minutes === 'number') exerciseTotalMin += p.duration_minutes;
    }
    if (ev.kind === 'hydration' && typeof p.volume_ml === 'number') {
      hydrationTotalMl += p.volume_ml;
    }
    if (ev.kind === 'symptom') symptomCount++;
    if (ev.kind === 'emotion' && typeof p.mood === 'string') {
      moodCounts[p.mood] = (moodCounts[p.mood] || 0) + 1;
    }
  }

  // Compute streak
  let streakDays = 0;
  const checkDate = new Date();
  while (true) {
    const ds = checkDate.toISOString().slice(0, 10);
    if (eventDates.has(ds)) {
      streakDays++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }

  return {
    totalEvents: activeEvents.length,
    periodDays,
    sleepTotalHours,
    sleepAvgHours: sleepDays > 0 ? Math.round((sleepTotalHours / sleepDays) * 10) / 10 : 0,
    sleepDays,
    exerciseCount,
    exerciseTotalMin,
    hydrationTotalMl,
    symptomCount,
    streakDays,
    moodCounts,
  };
}

function SummaryView({ events }: { events: readonly EventResponse[] }): JSX.Element {
  const stats = computeStats(events);
  const topMood = Object.entries(stats.moodCounts).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="summary-view">
      <div className="summary-grid">
        <div className="summary-card">
          <span className="summary-value">{stats.totalEvents}</span>
          <span className="summary-label">Events</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{stats.streakDays}</span>
          <span className="summary-label">Day streak</span>
        </div>
        <div className="summary-card">
          <span className="summary-value">{stats.periodDays}</span>
          <span className="summary-label">Days tracked</span>
        </div>
      </div>

      <div className="summary-section">
        <h3 className="summary-section-title">Sleep</h3>
        <div className="summary-stats">
          <div className="summary-stat">
            <span className="summary-stat-value">{stats.sleepAvgHours}h</span>
            <span className="summary-stat-label">Avg / night</span>
          </div>
          <div className="summary-stat">
            <span className="summary-stat-value">{stats.sleepTotalHours.toFixed(1)}h</span>
            <span className="summary-stat-label">Total</span>
          </div>
          <div className="summary-stat">
            <span className="summary-stat-value">{stats.sleepDays}</span>
            <span className="summary-stat-label">Nights logged</span>
          </div>
        </div>
      </div>

      <div className="summary-section">
        <h3 className="summary-section-title">Exercise</h3>
        <div className="summary-stats">
          <div className="summary-stat">
            <span className="summary-stat-value">{stats.exerciseCount}</span>
            <span className="summary-stat-label">Sessions</span>
          </div>
          <div className="summary-stat">
            <span className="summary-stat-value">{stats.exerciseTotalMin}min</span>
            <span className="summary-stat-label">Total time</span>
          </div>
          {stats.exerciseCount > 0 && (
            <div className="summary-stat">
              <span className="summary-stat-value">
                {Math.round(stats.exerciseTotalMin / stats.exerciseCount)}min
              </span>
              <span className="summary-stat-label">Avg / session</span>
            </div>
          )}
        </div>
      </div>

      <div className="summary-section">
        <h3 className="summary-section-title">Hydration</h3>
        <div className="summary-stats">
          <div className="summary-stat">
            <span className="summary-stat-value">
              {stats.hydrationTotalMl > 1000
                ? `${(stats.hydrationTotalMl / 1000).toFixed(1)}L`
                : `${stats.hydrationTotalMl}ml`}
            </span>
            <span className="summary-stat-label">Total water</span>
          </div>
        </div>
      </div>

      <div className="summary-section">
        <h3 className="summary-section-title">Symptoms</h3>
        <div className="summary-stats">
          <div className="summary-stat">
            <span className="summary-stat-value">{stats.symptomCount}</span>
            <span className="summary-stat-label">Reported</span>
          </div>
        </div>
      </div>

      {(() => {
        if (!topMood) return null;
        const [mood, count] = topMood;
        return (
          <div className="summary-section">
            <h3 className="summary-section-title">Mood</h3>
            <div className="summary-stats">
              <div className="summary-stat">
                <span className="summary-stat-value">{mood}</span>
                <span className="summary-stat-label">Most frequent ({count}x)</span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function confidenceLabel(confidence: string): string {
  return confidence.replace('-', ' ');
}

function experimentOutcomeLabel(outcome: ExperimentOutcome | null): string {
  return outcome ? outcome.replace('-', ' ') : 'pending';
}

function evidenceSummaryLabel(item: {
  readonly evidence_event_ids: readonly string[];
  readonly evidence_observation_ids: readonly string[];
  readonly evidence_experiment_ids: readonly string[];
}): string {
  const parts = [
    item.evidence_event_ids.length > 0
      ? `${item.evidence_event_ids.length} event${item.evidence_event_ids.length === 1 ? '' : 's'}`
      : null,
    item.evidence_observation_ids.length > 0
      ? `${item.evidence_observation_ids.length} observation${item.evidence_observation_ids.length === 1 ? '' : 's'}`
      : null,
    item.evidence_experiment_ids.length > 0
      ? `${item.evidence_experiment_ids.length} experiment${item.evidence_experiment_ids.length === 1 ? '' : 's'}`
      : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : 'No evidence references';
}

function hasExperimentEvidence(item: {
  readonly evidence_experiment_ids: readonly string[];
}): boolean {
  return item.evidence_experiment_ids.length > 0;
}

function ObservationsPanel({
  observations,
  drafts,
  loading,
  pendingAction,
  onSaveDraft,
  onArchiveObservation,
}: {
  readonly observations: readonly ObservationResponse[];
  readonly drafts: readonly ObservationDraftResponse[];
  readonly loading: boolean;
  readonly pendingAction: string | null;
  readonly onSaveDraft: (draft: ObservationDraftResponse, index: number) => void;
  readonly onArchiveObservation: (observation: ObservationResponse) => void;
}): JSX.Element | null {
  if (loading) {
    return (
      <section className="observations-panel">
        <div className="observations-header">
          <h2>Possible patterns</h2>
          <span>Looking for patterns...</span>
        </div>
      </section>
    );
  }

  if (observations.length === 0 && drafts.length === 0) {
    return null;
  }

  return (
    <section className="observations-panel">
      <div className="observations-header">
        <h2>Possible patterns</h2>
        <span>
          {observations.length} saved, {drafts.length} candidate{drafts.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="observation-list">
        {observations.slice(0, 3).map((observation) => (
          <article key={observation.id} className="observation-card">
            <div className="observation-card-top">
              <span className="observation-origin">{observation.origin}</span>
              <span className="observation-confidence">
                {confidenceLabel(observation.confidence)}
              </span>
            </div>
            <p>{observation.statement}</p>
            {observation.confidence_basis && <small>{observation.confidence_basis}</small>}
            <span className="observation-evidence">
              {observation.evidence_event_ids.length} evidence event
              {observation.evidence_event_ids.length === 1 ? '' : 's'}
            </span>
            <div className="observation-actions">
              <button
                type="button"
                className="observation-action-btn"
                onClick={() => onArchiveObservation(observation)}
                disabled={pendingAction === `archive:${observation.id}`}
              >
                {pendingAction === `archive:${observation.id}` ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          </article>
        ))}
        {drafts.slice(0, 3).map((draft, index) => (
          <article
            key={`${draft.statement}-${index}`}
            className="observation-card observation-card-draft"
          >
            <div className="observation-card-top">
              <span className="observation-origin">candidate</span>
              <span className="observation-confidence">{confidenceLabel(draft.confidence)}</span>
            </div>
            <p>{draft.statement}</p>
            <small>{draft.confidence_basis}</small>
            <span className="observation-evidence">
              {draft.evidence_event_ids.length} evidence event
              {draft.evidence_event_ids.length === 1 ? '' : 's'}
            </span>
            <div className="observation-actions">
              <button
                type="button"
                className="observation-action-btn observation-action-primary"
                onClick={() => onSaveDraft(draft, index)}
                disabled={pendingAction === `draft:${index}`}
              >
                {pendingAction === `draft:${index}` ? 'Saving...' : 'Save'}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function isPersistedPlan(
  plan: DailyPlanDraftResponse | DailyPlanResponse | null,
): plan is DailyPlanResponse {
  return Boolean(plan && 'id' in plan);
}

function TodayPlanPanel({
  plan,
  loading,
  pendingAction,
  onCommitPlan,
  onUpdateItemStatus,
}: {
  readonly plan: DailyPlanDraftResponse | DailyPlanResponse | null;
  readonly loading: boolean;
  readonly pendingAction: string | null;
  readonly onCommitPlan: () => void;
  readonly onUpdateItemStatus: (itemId: string, status: 'accepted' | 'declined' | 'done') => void;
}): JSX.Element {
  return (
    <section className="today-plan-panel">
      <div className="today-plan-header">
        <div>
          <h2>Today&apos;s breath prep</h2>
          <span>{loading ? 'Generating plan...' : plan ? plan.plan_date : 'No plan loaded'}</span>
        </div>
        {plan && !isPersistedPlan(plan) && (
          <button type="button" onClick={onCommitPlan} disabled={pendingAction === 'commit'}>
            {pendingAction === 'commit' ? 'Saving...' : 'Save prep'}
          </button>
        )}
      </div>
      {plan ? (
        <>
          <p className="today-plan-summary">{plan.summary}</p>
          <div className="today-plan-items">
            {plan.items.map((item) => (
              <article key={item.id} className="today-plan-item">
                <div className="today-plan-item-top">
                  <h3>{item.title}</h3>
                  <span>{item.status}</span>
                </div>
                <p>{item.description}</p>
                <small>{item.basis}</small>
                {isPersistedPlan(plan) && (
                  <div className="today-plan-actions">
                    <button
                      type="button"
                      onClick={() => onUpdateItemStatus(item.id, 'accepted')}
                      disabled={pendingAction === `${item.id}:accepted`}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => onUpdateItemStatus(item.id, 'done')}
                      disabled={pendingAction === `${item.id}:done`}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      onClick={() => onUpdateItemStatus(item.id, 'declined')}
                      disabled={pendingAction === `${item.id}:declined`}
                    >
                      Decline
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        </>
      ) : (
        <p className="today-plan-summary">Breath prep will appear here once you are signed in.</p>
      )}
    </section>
  );
}

function KnowledgePanel({
  knowledge,
  drafts,
  loading,
  draftStatement,
  pendingAction,
  onDraftStatementChange,
  onCreateKnowledge,
  onSaveDraft,
  onDismissDraft,
  onArchiveKnowledge,
}: {
  readonly knowledge: readonly KnowledgeResponse[];
  readonly drafts: readonly KnowledgeDraftResponse[];
  readonly loading: boolean;
  readonly draftStatement: string;
  readonly pendingAction: string | null;
  readonly onDraftStatementChange: (value: string) => void;
  readonly onCreateKnowledge: (event: FormEvent<HTMLFormElement>) => void;
  readonly onSaveDraft: (draft: KnowledgeDraftResponse, index: number) => void;
  readonly onDismissDraft: (draft: KnowledgeDraftResponse, index: number) => void;
  readonly onArchiveKnowledge: (item: KnowledgeResponse) => void;
}): JSX.Element {
  return (
    <section className="knowledge-panel">
      <div className="knowledge-header">
        <h2>What seems true</h2>
        <span>
          {loading
            ? 'Loading...'
            : `${knowledge.length} current, ${drafts.length} candidate${drafts.length === 1 ? '' : 's'}`}
        </span>
      </div>
      <form className="knowledge-form" onSubmit={onCreateKnowledge}>
        <input
          value={draftStatement}
          onChange={(event) => onDraftStatementChange(event.target.value)}
          placeholder="Something you believe is true for you"
          disabled={pendingAction === 'create'}
        />
        <button type="submit" disabled={pendingAction === 'create' || !draftStatement.trim()}>
          {pendingAction === 'create' ? 'Saving...' : 'Add'}
        </button>
      </form>
      {!loading && knowledge.length > 0 && (
        <div className="knowledge-list">
          {knowledge.slice(0, 3).map((item) => (
            <article key={item.id} className="knowledge-card">
              <div className="knowledge-card-top">
                <span className="knowledge-origin">{item.origin}</span>
                <span className="knowledge-confidence">{confidenceLabel(item.confidence)}</span>
              </div>
              <p>{item.statement}</p>
              {item.confidence_basis && <small>{item.confidence_basis}</small>}
              <span className="knowledge-evidence">{evidenceSummaryLabel(item)}</span>
              {hasExperimentEvidence(item) && (
                <span className="knowledge-evidence knowledge-evidence-experiment">
                  Experiment learning
                </span>
              )}
              <div className="knowledge-actions">
                <button
                  type="button"
                  className="knowledge-action-btn"
                  onClick={() => onArchiveKnowledge(item)}
                  disabled={pendingAction === `archive:${item.id}`}
                >
                  {pendingAction === `archive:${item.id}` ? 'Archiving...' : 'Archive'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
      {!loading && drafts.length > 0 && (
        <div className="knowledge-list">
          {drafts.slice(0, 3).map((draft, index) => (
            <article
              key={`${draft.statement}-${index}`}
              className="knowledge-card knowledge-card-draft"
            >
              <div className="knowledge-card-top">
                <span className="knowledge-origin">candidate</span>
                <span className="knowledge-confidence">{confidenceLabel(draft.confidence)}</span>
              </div>
              <p>{draft.statement}</p>
              <small>{draft.confidence_basis}</small>
              <span className="knowledge-evidence">{evidenceSummaryLabel(draft)}</span>
              {hasExperimentEvidence(draft) && (
                <span className="knowledge-evidence knowledge-evidence-experiment">
                  Experiment learning
                </span>
              )}
              <div className="knowledge-actions">
                <button
                  type="button"
                  className="knowledge-action-btn"
                  onClick={() => onDismissDraft(draft, index)}
                  disabled={pendingAction === `dismiss:${index}`}
                >
                  {pendingAction === `dismiss:${index}` ? 'Dismissing...' : 'Dismiss'}
                </button>
                <button
                  type="button"
                  className="knowledge-action-btn knowledge-action-primary"
                  onClick={() => onSaveDraft(draft, index)}
                  disabled={pendingAction === `draft:${index}`}
                >
                  {pendingAction === `draft:${index}` ? 'Saving...' : 'Save'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ExperimentsPanel({
  experiments,
  loading,
  draftHypothesis,
  pendingAction,
  onDraftHypothesisChange,
  onCreateExperiment,
  onUpdateExperimentStatus,
  onCompleteExperiment,
  outcomeNotes,
  onOutcomeNoteChange,
}: {
  readonly experiments: readonly ExperimentResponse[];
  readonly loading: boolean;
  readonly draftHypothesis: string;
  readonly pendingAction: string | null;
  readonly outcomeNotes: Readonly<Record<string, string>>;
  readonly onDraftHypothesisChange: (value: string) => void;
  readonly onCreateExperiment: (event: FormEvent<HTMLFormElement>) => void;
  readonly onUpdateExperimentStatus: (
    experiment: ExperimentResponse,
    status: 'active' | 'abandoned',
  ) => void;
  readonly onCompleteExperiment: (
    experiment: ExperimentResponse,
    outcome: ExperimentCompletionOutcome,
    notes: string,
  ) => void;
  readonly onOutcomeNoteChange: (experimentId: string, value: string) => void;
}): JSX.Element {
  return (
    <section className="experiments-panel">
      <div className="experiments-header">
        <h2>Small trials</h2>
        <span>{loading ? 'Loading...' : `${experiments.length} current`}</span>
      </div>
      <form className="experiments-form" onSubmit={onCreateExperiment}>
        <input
          value={draftHypothesis}
          onChange={(event) => onDraftHypothesisChange(event.target.value)}
          placeholder="Hypothesis to test gently"
          disabled={pendingAction === 'create'}
        />
        <button type="submit" disabled={pendingAction === 'create' || !draftHypothesis.trim()}>
          {pendingAction === 'create' ? 'Saving...' : 'Add'}
        </button>
      </form>
      {experiments.length > 0 && (
        <div className="experiments-list">
          {experiments.slice(0, 3).map((experiment) => (
            <article key={experiment.id} className="experiment-card">
              <div className="experiment-card-top">
                <span>{experiment.status}</span>
                <span>
                  {experiment.start_date} - {experiment.end_date}
                </span>
              </div>
              <h3>{experiment.hypothesis}</h3>
              <p>{experiment.protocol}</p>
              <small>{experiment.observation_target}</small>
              {experiment.status === 'completed' && (
                <div className="experiment-outcome">
                  <span>{experimentOutcomeLabel(experiment.outcome)}</span>
                  {experiment.outcome_notes && <p>{experiment.outcome_notes}</p>}
                </div>
              )}
              {experiment.status === 'active' && (
                <textarea
                  className="experiment-outcome-note"
                  value={outcomeNotes[experiment.id] ?? ''}
                  onChange={(event) => onOutcomeNoteChange(experiment.id, event.target.value)}
                  placeholder="What happened?"
                  rows={2}
                  disabled={pendingAction?.startsWith(`${experiment.id}:completed`) ?? false}
                />
              )}
              <div className="experiment-actions">
                {experiment.status === 'proposed' && (
                  <button
                    type="button"
                    onClick={() => onUpdateExperimentStatus(experiment, 'active')}
                    disabled={pendingAction === `${experiment.id}:active`}
                  >
                    Start
                  </button>
                )}
                {experiment.status !== 'abandoned' && experiment.status !== 'completed' && (
                  <button
                    type="button"
                    onClick={() => onUpdateExperimentStatus(experiment, 'abandoned')}
                    disabled={pendingAction === `${experiment.id}:abandoned`}
                  >
                    Abandon
                  </button>
                )}
                {experiment.status === 'active' && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        onCompleteExperiment(
                          experiment,
                          'supported',
                          outcomeNotes[experiment.id] ?? '',
                        )
                      }
                      disabled={pendingAction === `${experiment.id}:completed:supported`}
                    >
                      Supported
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        onCompleteExperiment(
                          experiment,
                          'unsupported',
                          outcomeNotes[experiment.id] ?? '',
                        )
                      }
                      disabled={pendingAction === `${experiment.id}:completed:unsupported`}
                    >
                      Unsupported
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        onCompleteExperiment(
                          experiment,
                          'inconclusive',
                          outcomeNotes[experiment.id] ?? '',
                        )
                      }
                      disabled={pendingAction === `${experiment.id}:completed:inconclusive`}
                    >
                      Inconclusive
                    </button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Page
// ---------------------------------------------------------------------------

export function DashboardPage(): JSX.Element {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<readonly EventResponse[]>([]);
  const [observations, setObservations] = useState<readonly ObservationResponse[]>([]);
  const [todayPlan, setTodayPlan] = useState<DailyPlanDraftResponse | DailyPlanResponse | null>(
    null,
  );
  const [todayPlanLoading, setTodayPlanLoading] = useState(false);
  const [todayPlanAction, setTodayPlanAction] = useState<string | null>(null);
  const [experiments, setExperiments] = useState<readonly ExperimentResponse[]>([]);
  const [experimentsLoading, setExperimentsLoading] = useState(false);
  const [experimentAction, setExperimentAction] = useState<string | null>(null);
  const [experimentDraftHypothesis, setExperimentDraftHypothesis] = useState('');
  const [experimentOutcomeNotes, setExperimentOutcomeNotes] = useState<
    Readonly<Record<string, string>>
  >({});
  const [observationDrafts, setObservationDrafts] = useState<readonly ObservationDraftResponse[]>(
    [],
  );
  const [observationsLoading, setObservationsLoading] = useState(false);
  const [observationAction, setObservationAction] = useState<string | null>(null);
  const [knowledge, setKnowledge] = useState<readonly KnowledgeResponse[]>([]);
  const [knowledgeDrafts, setKnowledgeDrafts] = useState<readonly KnowledgeDraftResponse[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeAction, setKnowledgeAction] = useState<string | null>(null);
  const [knowledgeDraftStatement, setKnowledgeDraftStatement] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showBreathProfile, setShowBreathProfile] = useState(false);
  const [activeDashboardTab, setActiveDashboardTab] = useState<'prep' | 'insights' | 'log'>('prep');
  const [initialAddKind, setInitialAddKind] = useState<string | null>(null);
  const [detailEvent, setDetailEvent] = useState<EventResponse | null>(null);
  const [editEvent, setEditEvent] = useState<EventResponse | null>(null);

  // -- Pagination --
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // -- Kind filter --
  const [kindFilter, setKindFilter] = useState('');

  // -- Date range filter --
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // -- Search --
  const [searchQuery, setSearchQuery] = useState('');

  // -- Batch operations --
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);

  // -- View mode (timeline vs summary) --
  const [showSummary, setShowSummary] = useState(false);

  // -- Archived events toggle --
  const [showArchived, setShowArchived] = useState(false);

  // -- Undo delete --
  const [deletedEvent, setDeletedEvent] = useState<EventResponse | null>(null);
  const [showUndo, setShowUndo] = useState(false);
  const [undoAction, setUndoAction] = useState<'deleted' | 'archived'>('deleted');
  const [undoExpiresAt, setUndoExpiresAt] = useState(Date.now());
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const PAGE_SIZE = 25;

  const fetchEvents = useCallback(
    async (params?: { cursor?: string; append?: boolean }) => {
      if (params?.append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const data: EventListResponse = await api.listEvents({
          limit: PAGE_SIZE,
          cursor: params?.cursor,
          kind: kindFilter || undefined,
          occurred_at_gte: startDate ? `${startDate}T00:00:00Z` : undefined,
          occurred_at_lte: endDate ? `${endDate}T23:59:59Z` : undefined,
          include_archived: showArchived,
        });

        if (params?.append) {
          setEvents((prev) => [...prev, ...data.items]);
        } else {
          setEvents(data.items);
        }

        setNextCursor(data.next_cursor);
        setHasMore(data.has_more);
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to load events. Is the API server running?');
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [kindFilter, startDate, endDate, showArchived],
  );

  const fetchObservations = useCallback(async () => {
    setObservationsLoading(true);
    try {
      const [saved, preview] = await Promise.all([
        api.listObservations({ limit: 5 }),
        api.previewObservations({ limit: 200 }),
      ]);
      setObservations(saved.items);
      setObservationDrafts(preview.items);
    } catch {
      setObservations([]);
      setObservationDrafts([]);
    } finally {
      setObservationsLoading(false);
    }
  }, []);

  const fetchTodayPlan = useCallback(async () => {
    setTodayPlanLoading(true);
    try {
      const data = await api.getTodayPlan();
      setTodayPlan(data);
    } catch {
      setTodayPlan(null);
    } finally {
      setTodayPlanLoading(false);
    }
  }, []);

  const fetchExperiments = useCallback(async () => {
    setExperimentsLoading(true);
    try {
      const data = await api.listExperiments({ limit: 5 });
      setExperiments(data.items);
    } catch {
      setExperiments([]);
    } finally {
      setExperimentsLoading(false);
    }
  }, []);

  const fetchKnowledge = useCallback(async () => {
    setKnowledgeLoading(true);
    try {
      const [data, preview, experimentLearning] = await Promise.all([
        api.listKnowledge({ limit: 5, include_stale: true }),
        api.previewKnowledge({ limit: 200 }),
        api.previewExperimentLearning({ limit: 100 }),
      ]);
      setKnowledge(data.items);
      setKnowledgeDrafts([...preview.items, ...experimentLearning.items]);
    } catch {
      setKnowledge([]);
      setKnowledgeDrafts([]);
    } finally {
      setKnowledgeLoading(false);
    }
  }, []);

  // Fetch initial events when auth is ready or kind filter changes
  useEffect(() => {
    if (!authLoading && user) {
      setNextCursor(null);
      setHasMore(false);
      fetchEvents({});
      fetchTodayPlan();
      fetchExperiments();
      fetchObservations();
      fetchKnowledge();
    }
  }, [
    authLoading,
    user,
    fetchEvents,
    fetchTodayPlan,
    fetchExperiments,
    fetchObservations,
    fetchKnowledge,
  ]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const loadMore = useCallback(() => {
    if (nextCursor && !loadingMore) {
      fetchEvents({ cursor: nextCursor, append: true });
    }
  }, [nextCursor, loadingMore, fetchEvents]);

  const handleEventCreated = () => {
    setShowForm(false);
    setInitialAddKind(null);
    setNextCursor(null);
    fetchEvents({});
    fetchTodayPlan();
    fetchExperiments();
    fetchObservations();
    fetchKnowledge();
  };

  const handleEventUpdated = () => {
    setEditEvent(null);
    fetchEvents({});
    fetchTodayPlan();
    fetchExperiments();
    fetchObservations();
    fetchKnowledge();
  };

  const handleCreateExperiment = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const hypothesis = experimentDraftHypothesis.trim();
      if (!hypothesis) return;

      const start = new Date();
      const end = new Date(start);
      end.setDate(start.getDate() + 5);
      setExperimentAction('create');
      setError(null);
      try {
        await api.createExperiment({
          hypothesis,
          purpose: 'Learn whether this hypothesis is useful for you.',
          protocol:
            'Try one small, reversible change for five days and keep logging relevant events.',
          observation_target: 'Relevant symptom, journal, and context notes during the experiment.',
          start_date: start.toISOString().slice(0, 10),
          end_date: end.toISOString().slice(0, 10),
          confidence: 'very-low',
          confidence_basis: 'User-authored experiment.',
        });
        setExperimentDraftHypothesis('');
        await fetchExperiments();
        await fetchTodayPlan();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to create experiment.');
        }
      } finally {
        setExperimentAction(null);
      }
    },
    [experimentDraftHypothesis, fetchExperiments, fetchTodayPlan],
  );

  const handleUpdateExperimentStatus = useCallback(
    async (experiment: ExperimentResponse, status: 'active' | 'abandoned') => {
      const actionKey = `${experiment.id}:${status}`;
      setExperimentAction(actionKey);
      setError(null);
      try {
        await api.updateExperimentStatus(experiment.id, { status });
        await fetchExperiments();
        await fetchTodayPlan();
        await fetchKnowledge();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to update experiment.');
        }
      } finally {
        setExperimentAction(null);
      }
    },
    [fetchExperiments, fetchKnowledge, fetchTodayPlan],
  );

  const handleCompleteExperiment = useCallback(
    async (experiment: ExperimentResponse, outcome: ExperimentCompletionOutcome, notes: string) => {
      const actionKey = `${experiment.id}:completed:${outcome}`;
      setExperimentAction(actionKey);
      setError(null);
      const trimmedNotes = notes.trim();
      try {
        await api.updateExperimentStatus(experiment.id, {
          status: 'completed',
          outcome,
          outcome_notes: trimmedNotes || null,
        });
        setExperimentOutcomeNotes((current) => {
          const next = { ...current };
          delete next[experiment.id];
          return next;
        });
        await fetchExperiments();
        await fetchTodayPlan();
        await fetchKnowledge();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to complete experiment.');
        }
      } finally {
        setExperimentAction(null);
      }
    },
    [fetchExperiments, fetchKnowledge, fetchTodayPlan],
  );

  const handleExperimentOutcomeNoteChange = useCallback((experimentId: string, value: string) => {
    setExperimentOutcomeNotes((current) => ({ ...current, [experimentId]: value }));
  }, []);

  const handleCommitTodayPlan = useCallback(async () => {
    if (!todayPlan) return;
    setTodayPlanAction('commit');
    setError(null);
    try {
      const saved = await api.commitTodayPlan(todayPlan.plan_date);
      setTodayPlan(saved);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'status' in err) {
        const apiErr = err as { status: number; detail: string };
        setError(apiErr.detail || `Request failed (${apiErr.status})`);
      } else {
        setError("Failed to save today's plan.");
      }
    } finally {
      setTodayPlanAction(null);
    }
  }, [todayPlan]);

  const handleUpdatePlanItemStatus = useCallback(
    async (itemId: string, status: 'accepted' | 'declined' | 'done') => {
      if (!isPersistedPlan(todayPlan)) return;
      const actionKey = `${itemId}:${status}`;
      setTodayPlanAction(actionKey);
      setError(null);
      try {
        const updated = await api.updatePlanItemStatus(todayPlan.id, itemId, status);
        setTodayPlan(updated);
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to update plan item.');
        }
      } finally {
        setTodayPlanAction(null);
      }
    },
    [todayPlan],
  );

  const handleSaveObservationDraft = useCallback(
    async (draft: ObservationDraftResponse, index: number) => {
      const actionKey = `draft:${index}`;
      setObservationAction(actionKey);
      setError(null);
      try {
        await api.createObservation({
          statement: draft.statement,
          evidence_event_ids: draft.evidence_event_ids,
          confidence: draft.confidence,
          confidence_basis: draft.confidence_basis,
        });
        await fetchObservations();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to save observation.');
        }
      } finally {
        setObservationAction(null);
      }
    },
    [fetchObservations],
  );

  const handleArchiveObservation = useCallback(
    async (observation: ObservationResponse) => {
      const actionKey = `archive:${observation.id}`;
      setObservationAction(actionKey);
      setError(null);
      try {
        await api.archiveObservation(observation.id);
        await fetchObservations();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to archive observation.');
        }
      } finally {
        setObservationAction(null);
      }
    },
    [fetchObservations],
  );

  const handleCreateKnowledge = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const statement = knowledgeDraftStatement.trim();
      if (!statement) return;

      setKnowledgeAction('create');
      setError(null);
      try {
        await api.createKnowledge({
          statement,
          confidence: 'very-low',
          confidence_basis: 'User-authored Knowledge assertion.',
        });
        setKnowledgeDraftStatement('');
        await fetchKnowledge();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to save Knowledge.');
        }
      } finally {
        setKnowledgeAction(null);
      }
    },
    [fetchKnowledge, knowledgeDraftStatement],
  );

  const handleArchiveKnowledge = useCallback(
    async (item: KnowledgeResponse) => {
      const actionKey = `archive:${item.id}`;
      setKnowledgeAction(actionKey);
      setError(null);
      try {
        await api.archiveKnowledge(item.id);
        await fetchKnowledge();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to archive Knowledge.');
        }
      } finally {
        setKnowledgeAction(null);
      }
    },
    [fetchKnowledge],
  );

  const handleSaveKnowledgeDraft = useCallback(
    async (draft: KnowledgeDraftResponse, index: number) => {
      const actionKey = `draft:${index}`;
      setKnowledgeAction(actionKey);
      setError(null);
      try {
        if (draft.candidate_source === 'observations') {
          await api.commitKnowledgeCandidate(draft.statement);
        } else {
          await api.commitExperimentLearning(draft.statement);
        }
        await fetchKnowledge();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to save Knowledge candidate.');
        }
      } finally {
        setKnowledgeAction(null);
      }
    },
    [fetchKnowledge],
  );

  const handleDismissKnowledgeDraft = useCallback(
    async (draft: KnowledgeDraftResponse, index: number) => {
      const actionKey = `dismiss:${index}`;
      setKnowledgeAction(actionKey);
      setError(null);
      try {
        await api.dismissKnowledgeCandidate(draft.statement, draft.candidate_source);
        await fetchKnowledge();
      } catch (err: unknown) {
        if (err && typeof err === 'object' && 'status' in err) {
          const apiErr = err as { status: number; detail: string };
          setError(apiErr.detail || `Request failed (${apiErr.status})`);
        } else {
          setError('Failed to dismiss Knowledge candidate.');
        }
      } finally {
        setKnowledgeAction(null);
      }
    },
    [fetchKnowledge],
  );

  const handleShowDetail = useCallback((event: EventResponse) => {
    setDetailEvent(event);
  }, []);

  const handleDetailClose = useCallback(() => {
    setDetailEvent(null);
  }, []);

  const handleDetailEdit = useCallback(() => {
    setDetailEvent((current) => {
      if (current) {
        setEditEvent(current);
      }
      return null;
    });
  }, []);

  // Cleanup undo timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // -- Batch operations --
  const toggleSelectEvent = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectMode = useCallback(() => {
    setSelectMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(
      `Delete ${selectedIds.size} selected event${selectedIds.size !== 1 ? 's' : ''}?`,
    );
    if (!confirmed) return;
    const results = await Promise.allSettled([...selectedIds].map((id) => api.deleteEvent(id)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      setError(`${failed} event${failed !== 1 ? 's' : ''} could not be deleted.`);
    }
    setSelectedIds(new Set());
    setSelectMode(false);
    setNextCursor(null);
    fetchEvents({});
  }, [selectedIds, fetchEvents]);

  const handleBulkArchive = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(
      `Archive ${selectedIds.size} selected event${selectedIds.size !== 1 ? 's' : ''}?`,
    );
    if (!confirmed) return;
    const results = await Promise.allSettled([...selectedIds].map((id) => api.archiveEvent(id)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      setError(`${failed} event${failed !== 1 ? 's' : ''} could not be archived.`);
    }
    setSelectedIds(new Set());
    setSelectMode(false);
    setNextCursor(null);
    fetchEvents({});
  }, [selectedIds, fetchEvents]);

  // -- Export handlers --
  const handleExportCSV = useCallback(() => {
    const headers = [
      'id',
      'kind',
      'occurred_at',
      'recorded_at',
      'updated_at',
      'is_future',
      'archived_at',
      'context',
      'description',
      'type',
      'hours',
      'quality',
      'duration_minutes',
      'distance_km',
      'calories_est',
      'meal_type',
      'beverage_type',
      'volume_ml',
      'symptom_type',
      'severity',
      'mood',
      'intensity',
      'exercise_type',
      'treatment_type',
      'oral_care_type',
      'medical_type',
      'social_type',
      'journal_type',
      'travel_type',
    ];
    const rows = events.map((e) => {
      const p = e.payload ?? {};
      const row = [
        e.id,
        e.kind,
        e.occurred_at,
        e.recorded_at,
        e.updated_at,
        e.is_future ? 'true' : '',
        e.archived_at ?? '',
        e.context ?? '',
        p.description ?? '',
        p.type ?? '',
        p.hours ?? '',
        p.quality ?? '',
        p.duration_minutes ?? '',
        p.distance_km ?? '',
        p.calories_est ?? '',
        p.meal_type ?? '',
        p.beverage_type ?? '',
        p.volume_ml ?? '',
        p.symptom_type ?? '',
        p.severity ?? '',
        p.mood ?? '',
        p.intensity ?? '',
        p.exercise_type ?? '',
        p.treatment_type ?? '',
        p.oral_care_type ?? '',
        p.medical_type ?? '',
        p.social_type ?? '',
        p.journal_type ?? '',
        p.travel_type ?? '',
      ];
      return row;
    });
    const csv = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `breathly-events-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [events]);

  const handleExportJSON = useCallback(() => {
    const json = JSON.stringify(events, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `breathly-events-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [events]);

  // -- Undo actions --
  const handleEventDeleted = useCallback(async (event: EventResponse) => {
    // Close any open modals
    setDetailEvent(null);
    setEditEvent(null);

    // Store the event and show undo toast
    setDeletedEvent(event);
    setUndoAction('deleted');
    setShowUndo(true);

    // Clear previous timer
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
    }

    // Set timer to actually delete after 5 seconds
    setUndoExpiresAt(Date.now() + 5000);
    undoTimerRef.current = setTimeout(async () => {
      try {
        await api.deleteEvent(event.id);
        setEvents((prev) => prev.filter((e) => e.id !== event.id));
      } catch {
        // Silently fail
      }
      setDeletedEvent(null);
      setShowUndo(false);
    }, 5000);
  }, []);

  const handleEventArchived = useCallback(async (event: EventResponse) => {
    // Close any open modals
    setDetailEvent(null);
    setEditEvent(null);

    // Store the event and show undo toast
    setDeletedEvent(event);
    setUndoAction('archived');
    setShowUndo(true);

    // Clear previous timer
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
    }

    // Set timer to actually archive after 5 seconds
    setUndoExpiresAt(Date.now() + 5000);
    undoTimerRef.current = setTimeout(async () => {
      try {
        await api.archiveEvent(event.id);
        setEvents((prev) => prev.filter((e) => e.id !== event.id));
      } catch {
        // Silently fail
      }
      setDeletedEvent(null);
      setShowUndo(false);
    }, 5000);
  }, []);

  const handleUndoDelete = useCallback(async () => {
    if (!deletedEvent) return;

    // Clear timer — event stays in DB
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }

    setDeletedEvent(null);
    setShowUndo(false);
  }, [deletedEvent]);

  const handleKindFilterChange = useCallback((kind: string) => {
    setKindFilter(kind);
  }, []);

  const handleStartDateChange = useCallback((val: string) => {
    setStartDate(val);
    setNextCursor(null);
  }, []);

  const handleEndDateChange = useCallback((val: string) => {
    setEndDate(val);
    setNextCursor(null);
  }, []);

  const toggleView = useCallback(() => {
    setShowSummary((prev) => !prev);
  }, []);

  const openAddForm = useCallback((kind?: string) => {
    setInitialAddKind(kind ?? null);
    setShowForm(true);
  }, []);

  const closeAddForm = useCallback(() => {
    setShowForm(false);
    setInitialAddKind(null);
  }, []);

  const clearTimelineFilters = useCallback(() => {
    setSearchQuery('');
    setKindFilter('');
    setStartDate('');
    setEndDate('');
    setShowArchived(false);
    setNextCursor(null);
  }, []);

  // -- Keyboard shortcuts --
  useEffect(() => {
    if (authLoading || !user) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      )
        return;
      if (e.key === 'n' || e.key === 'N') {
        openAddForm();
        return;
      }
      if (e.key === 'Escape') {
        if (showForm) {
          closeAddForm();
          return;
        }
        if (editEvent) {
          setEditEvent(null);
          return;
        }
        if (detailEvent) {
          setDetailEvent(null);
          return;
        }
      }
      if (e.key === 's' || e.key === 'S') {
        toggleView();
        return;
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [authLoading, user, showForm, editEvent, detailEvent, openAddForm, closeAddForm, toggleView]);

  if (authLoading) {
    return (
      <section className="dashboard">
        <div className="dashboard-loading">Loading...</div>
      </section>
    );
  }

  // Client-side search filtering
  const filteredEvents = searchQuery
    ? events.filter((ev) => {
        const desc = typeof ev.payload?.description === 'string' ? ev.payload.description : '';
        const ctx = ev.context ?? '';
        const term = searchQuery.toLowerCase();
        return (
          ev.kind.toLowerCase().includes(term) ||
          desc.toLowerCase().includes(term) ||
          ctx.toLowerCase().includes(term)
        );
      })
    : events;

  // -- Skeleton loading state --
  if (loading && events.length === 0 && !error) {
    return (
      <section className="dashboard">
        <header className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Today&apos;s breath prep</h1>
            <p className="dashboard-meta">Loading...</p>
          </div>
        </header>
        <div className="skeleton-list">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-icon" />
              <div className="skeleton-body">
                <div className="skeleton-line skeleton-line-title" />
                <div className="skeleton-line skeleton-line-detail" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error && events.length === 0) {
    return (
      <section className="dashboard">
        <div className="dashboard-error">
          <p>{error}</p>
          <button className="retry-btn" onClick={() => fetchEvents({})}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  const dayGroups = groupByDay(filteredEvents);
  const breathProfile = latestBreathProfile(events);

  return (
    <section className="dashboard">
      <header className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Halitosis care cockpit</h1>
          <p className="dashboard-meta">
            {events.length} breath log{events.length !== 1 ? 's' : ''}
            {showArchived &&
              (() => {
                const archivedCount = events.filter((e) => e.archived_at).length;
                return archivedCount > 0 ? ` (${archivedCount} archived)` : '';
              })()}
          </p>
        </div>
        <div className="dashboard-header-actions">
          <button
            className={`select-btn ${selectMode ? 'select-btn-active' : ''}`}
            onClick={toggleSelectMode}
          >
            {selectMode ? 'Done' : 'Select'}
          </button>
          {selectMode && selectedIds.size > 0 && (
            <>
              <button className="bulk-archive-btn" onClick={handleBulkArchive}>
                Archive {selectedIds.size}
              </button>
              <button className="bulk-delete-btn" onClick={handleBulkDelete}>
                Delete {selectedIds.size}
              </button>
            </>
          )}
          <button className="view-toggle-btn" onClick={toggleView}>
            {showSummary ? 'Breath log' : 'Summary'}
          </button>
          <button
            className={`archive-toggle-btn ${showArchived ? 'archive-toggle-btn-active' : ''}`}
            onClick={() => setShowArchived((prev) => !prev)}
          >
            {showArchived ? 'Active' : 'Archived'}
          </button>
          <button className="add-event-btn" onClick={() => openAddForm('symptom')}>
            Check in
          </button>
        </div>
      </header>

      <div className="dashboard-cockpit">
        <BreathCommandCenter
          events={events}
          observations={observations}
          experiments={experiments}
          onCheckIn={() => openAddForm('symptom')}
        />

        <BreathProfileCard profile={breathProfile} onOpen={() => setShowBreathProfile(true)} />
      </div>

      <nav className="dashboard-tabs" aria-label="Dashboard sections">
        {(
          [
            ['prep', 'Aid plan'],
            ['insights', 'Signals'],
            ['log', 'Records'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={
              activeDashboardTab === key ? 'dashboard-tab dashboard-tab-active' : 'dashboard-tab'
            }
            onClick={() => setActiveDashboardTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>

      {activeDashboardTab === 'prep' && (
        <div className="dashboard-tab-panel dashboard-tab-panel-prep">
          <QuickLogRow
            onLogged={() => {
              setNextCursor(null);
              fetchEvents({});
              fetchTodayPlan();
              fetchExperiments();
              fetchObservations();
              fetchKnowledge();
            }}
          />
          <TailoredRegimePanel profile={breathProfile} />
          <RetentionPanel profile={breathProfile} />
          <TodayPlanPanel
            plan={todayPlan}
            loading={todayPlanLoading}
            pendingAction={todayPlanAction}
            onCommitPlan={handleCommitTodayPlan}
            onUpdateItemStatus={handleUpdatePlanItemStatus}
          />
        </div>
      )}

      {activeDashboardTab === 'insights' && (
        <div className="dashboard-tab-panel dashboard-tab-panel-insights">
          <ExperimentsPanel
            experiments={experiments}
            loading={experimentsLoading}
            draftHypothesis={experimentDraftHypothesis}
            pendingAction={experimentAction}
            outcomeNotes={experimentOutcomeNotes}
            onDraftHypothesisChange={setExperimentDraftHypothesis}
            onCreateExperiment={handleCreateExperiment}
            onUpdateExperimentStatus={handleUpdateExperimentStatus}
            onCompleteExperiment={handleCompleteExperiment}
            onOutcomeNoteChange={handleExperimentOutcomeNoteChange}
          />
          <ObservationsPanel
            observations={observations}
            drafts={observationDrafts}
            loading={observationsLoading}
            pendingAction={observationAction}
            onSaveDraft={handleSaveObservationDraft}
            onArchiveObservation={handleArchiveObservation}
          />
          <KnowledgePanel
            knowledge={knowledge}
            drafts={knowledgeDrafts}
            loading={knowledgeLoading}
            draftStatement={knowledgeDraftStatement}
            pendingAction={knowledgeAction}
            onDraftStatementChange={setKnowledgeDraftStatement}
            onCreateKnowledge={handleCreateKnowledge}
            onSaveDraft={handleSaveKnowledgeDraft}
            onDismissDraft={handleDismissKnowledgeDraft}
            onArchiveKnowledge={handleArchiveKnowledge}
          />
        </div>
      )}

      {activeDashboardTab === 'log' && (
        <div className="dashboard-tab-panel dashboard-tab-panel-log">
          <div className="dashboard-toolbar">
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
            <div className="export-buttons">
              <button className="export-btn" onClick={handleExportCSV} title="Export as CSV">
                CSV
              </button>
              <button className="export-btn" onClick={handleExportJSON} title="Export as JSON">
                JSON
              </button>
            </div>
          </div>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartChange={handleStartDateChange}
            onEndChange={handleEndDateChange}
          />
          <KindFilter value={kindFilter} onChange={handleKindFilterChange} />
        </div>
      )}

      {activeDashboardTab === 'log' && filteredEvents.length === 0 && !loading && (
        <EmptyTimelineState
          hasFilters={Boolean(searchQuery || startDate || endDate || kindFilter || showArchived)}
          message={
            searchQuery
              ? 'No events match your search.'
              : startDate || endDate
                ? 'No events in this date range.'
                : kindFilter
                  ? 'No matching events found.'
                  : 'No archived events found.'
          }
          onClearFilters={clearTimelineFilters}
          onStartWithKind={openAddForm}
        />
      )}

      {activeDashboardTab === 'log' && !showSummary && (
        <div className="timeline">
          {dayGroups.map((group) => (
            <div key={group.date} className="day-group">
              <div className="day-header">
                <span className="day-label">{group.label}</span>
                <span className="day-date">{group.date}</span>
              </div>
              <div className="day-events">
                {group.events.map((ev) => (
                  <EventCard
                    key={ev.id}
                    event={ev}
                    onShowDetail={handleShowDetail}
                    selected={selectedIds.has(ev.id)}
                    onToggleSelect={toggleSelectEvent}
                    selectMode={selectMode}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeDashboardTab === 'log' && !showSummary && hasMore && !loading && (
        <div className="load-more-wrap">
          <button className="load-more-btn" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}

      {activeDashboardTab === 'log' && showSummary && !showForm && filteredEvents.length > 0 && (
        <SummaryView events={filteredEvents} />
      )}

      {showForm && (
        <AddEventForm
          initialKind={initialAddKind}
          onClose={closeAddForm}
          onCreated={handleEventCreated}
        />
      )}

      {detailEvent && !editEvent && (
        <EventDetailView
          event={detailEvent}
          onClose={handleDetailClose}
          onEdit={handleDetailEdit}
          onDeleted={handleEventDeleted}
          onArchived={handleEventArchived}
        />
      )}

      {editEvent && (
        <EditEventForm
          event={editEvent}
          onClose={() => setEditEvent(null)}
          onUpdated={handleEventUpdated}
          onDeleted={handleEventDeleted}
        />
      )}

      {showBreathProfile && (
        <BreathProfileModal
          current={breathProfile}
          onClose={() => setShowBreathProfile(false)}
          onSaved={() => {
            setShowBreathProfile(false);
            setNextCursor(null);
            fetchEvents({});
          }}
        />
      )}

      <UndoToast
        kind={deletedEvent?.kind ?? ''}
        action={undoAction}
        visible={showUndo}
        expiresAt={undoExpiresAt}
        onUndo={handleUndoDelete}
      />
    </section>
  );
}
